import { useAuth } from '@clerk/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import { useProblemLists } from '@/components/problem-lists/problem-list-context'
import { ProblemsFilterPopover } from '@/components/problems/problems-filter-popover'
import { ProblemsSearchBar } from '@/components/problems/problems-search-bar'
import { ProblemsSortPopover } from '@/components/problems/problems-sort-popover'
import {
  applyFilters,
  type BrowseProblem,
  createProblemsFuse,
  defaultFilters,
  deriveAvailableDifficulties,
  deriveAvailableTopics,
  type FilterValues,
  type SortOption,
  searchProblems,
  sortProblems,
} from '@/components/problems/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'

// --- Session storage persistence for add-problems modal ---
interface PersistedAddProblemsState {
  searchQuery: string
  matchMode: 'any' | 'all'
  filters: FilterValues
  sortBy: SortOption
  sortDir: 'asc' | 'desc'
}

const DEFAULT_ADD_PROBLEMS_STATE: PersistedAddProblemsState = {
  searchQuery: '',
  matchMode: 'all',
  filters: { ...defaultFilters },
  sortBy: 'questionId',
  sortDir: 'asc',
}

function getPersistedAddProblemsState(
  listId: string,
): PersistedAddProblemsState {
  if (typeof window === 'undefined') return DEFAULT_ADD_PROBLEMS_STATE
  try {
    const stored = sessionStorage.getItem(`bctci-add-problems-${listId}`)
    if (stored) return JSON.parse(stored) as PersistedAddProblemsState
  } catch {
    // ignore
  }
  return DEFAULT_ADD_PROBLEMS_STATE
}

function persistAddProblemsState(
  listId: string,
  state: PersistedAddProblemsState,
): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(
      `bctci-add-problems-${listId}`,
      JSON.stringify(state),
    )
  } catch {
    // ignore
  }
}

interface AddProblemsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  listId: string
}

export function AddProblemsModal({
  open,
  onOpenChange,
  listId,
}: AddProblemsModalProps) {
  const { isSignedIn } = useAuth()
  const listCtx = useProblemLists()

  // --- Local search/filter/sort state (restored from sessionStorage) ---
  const [searchQuery, setSearchQuery] = useState(
    () => getPersistedAddProblemsState(listId).searchQuery,
  )
  const [filters, setFilters] = useState<FilterValues>(
    () => getPersistedAddProblemsState(listId).filters,
  )
  const [matchMode, setMatchMode] = useState<'any' | 'all'>(
    () => getPersistedAddProblemsState(listId).matchMode,
  )
  const [sortBy, setSortBy] = useState<SortOption>(
    () => getPersistedAddProblemsState(listId).sortBy,
  )
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(
    () => getPersistedAddProblemsState(listId).sortDir,
  )

  useEffect(() => {
    persistAddProblemsState(listId, {
      searchQuery,
      matchMode,
      filters,
      sortBy,
      sortDir,
    })
  }, [listId, searchQuery, matchMode, filters, sortBy, sortDir])

  const availableLists = useMemo(() => {
    return [...listCtx.lists]
      .filter((l) => l.id !== listId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((l) => ({ id: l.id, name: l.name }))
  }, [listCtx.lists, listId])

  // Fetch all browsable problems
  const {
    data: allProblems,
    isLoading,
    error,
  } = trpc.problems.browse.useQuery()

  // Fetch user progress for status filter
  const { data: userProgress } = trpc.problems.getUserProgress.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  const problemListIds = useMemo(() => {
    const set = new Set<string>()
    for (const p of allProblems ?? []) {
      const lists = listCtx.getListsForProblem(p.id)
      if (lists.includes(listId)) {
        set.add(p.id)
      }
    }
    return set
  }, [allProblems, listCtx, listId])

  // --- Search / Filter / Sort pipeline ---

  const problems: BrowseProblem[] = useMemo(
    () => allProblems ?? [],
    [allProblems],
  )

  const fuse = useMemo(() => createProblemsFuse(problems), [problems])

  const displayProblems = useMemo(() => {
    const searched = searchProblems(problems, fuse, searchQuery)
    const filtered = applyFilters(
      searched,
      filters,
      matchMode,
      userProgress?.progress,
      listCtx.memberships,
    )
    return sortProblems(filtered, sortBy, sortDir, userProgress?.progress)
  }, [
    problems,
    fuse,
    searchQuery,
    filters,
    matchMode,
    userProgress,
    sortBy,
    sortDir,
    listCtx.memberships,
  ])

  const availableTopics = useMemo(
    () => deriveAvailableTopics(problems),
    [problems],
  )

  const availableDifficulties = useMemo(
    () => deriveAvailableDifficulties(problems),
    [problems],
  )

  // --- Filter helpers ---

  const toggleFilterValue = useCallback(
    (field: 'status' | 'difficulty' | 'topics' | 'lists', value: string) => {
      setFilters((prev) => {
        const current = prev[field].values
        return {
          ...prev,
          [field]: {
            ...prev[field],
            values: current.includes(value)
              ? current.filter((v) => v !== value)
              : [...current, value],
          },
        }
      })
    },
    [],
  )

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters)
    setMatchMode('all')
  }, [])

  const handleToggle = useCallback(
    (problemId: string) => {
      listCtx.toggleProblemInList(listId, problemId)
    },
    [listCtx, listId],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add problems to list</DialogTitle>
        </DialogHeader>

        {/* Toolbar — search, sort, filter, bug icon */}
        <div className="flex items-center gap-1">
          <ProblemsSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search problems..."
          />
          <ProblemsSortPopover
            sortBy={sortBy}
            sortDir={sortDir}
            onSortChange={(newSortBy, newSortDir) => {
              setSortBy(newSortBy as SortOption)
              setSortDir(newSortDir)
            }}
            sortOptions={[
              { value: 'difficulty', label: 'Difficulty' },
              { value: 'questionId', label: 'Question ID' },
              { value: 'status', label: 'Status' },
            ]}
          />
          <ProblemsFilterPopover
            filters={filters}
            matchMode={matchMode}
            onMatchModeChange={setMatchMode}
            onToggleFilter={toggleFilterValue}
            onOperatorChange={(field, op) =>
              setFilters((prev) => ({
                ...prev,
                [field]: { ...prev[field], operator: op },
              }))
            }
            availableTopics={availableTopics}
            availableDifficulties={availableDifficulties}
            availableLists={availableLists}
            onReset={resetFilters}
          />
          <div className="flex-1" />
          {isSignedIn && <FeaturebaseBugButton />}
        </div>

        {/* Problem list */}
        <div className="max-h-96 overflow-y-auto">
          {isLoading && (
            <div className="text-muted-foreground py-8 text-center text-sm">
              Loading problems...
            </div>
          )}
          {error && (
            <div className="text-destructive py-8 text-center text-sm">
              Failed to load problems. Please try again.
            </div>
          )}
          {!isLoading && !error && displayProblems.length === 0 && (
            <div className="text-muted-foreground py-8 text-center text-sm">
              {searchQuery
                ? 'No problems match your search or filters.'
                : 'No problems available.'}
            </div>
          )}
          {displayProblems.map((p) => {
            const isInList = problemListIds.has(p.id)
            return (
              <label
                key={p.id}
                className="hover:bg-muted flex cursor-pointer items-center gap-3 rounded px-3 py-2 text-sm"
              >
                <input
                  type="checkbox"
                  checked={isInList}
                  onChange={() => handleToggle(p.id)}
                  className="accent-primary h-4 w-4 shrink-0 rounded"
                />
                <span className="flex-1 truncate">
                  {p.externalId && (
                    <span className="text-muted-foreground mr-1">
                      {Number(p.externalId)}.
                    </span>
                  )}
                  {p.title}
                </span>
                <span
                  className={cn(
                    'text-xs font-medium',
                    p.difficulty?.name === 'Easy' && 'text-green-500',
                    p.difficulty?.name === 'Medium' && 'text-yellow-500',
                    p.difficulty?.name === 'Hard' && 'text-red-500',
                  )}
                >
                  {p.difficulty?.name === 'Medium'
                    ? 'Med.'
                    : p.difficulty?.name}
                </span>
              </label>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
